import { FIND_A_TENDER_TYPE, MANUAL_OPP_TYPES, MANUAL_TYPE, OCDS_TYPE, SAM_GOV_TYPE, US_GOV_CON_TYPE } from "constants/opportunities";
import { INITIAL_NEW_OPPORTUNITY } from "pages/capture/TrackedOpportunities/constants";
import { formatDateArrayToYMD } from "utils";

const TEXT_FIELDS = [
  "title",
  "description",
  "departmentAgency",
  "customer",
  "subTier",
  "office",
  "externalId",
  "naicsCode",
  "popStreetAddress",
  "popCity",
  "popState",
  "popCountry",
  "popZip",
  "city",
  "state",
  "countryCode",
  "zipCode",
  "primaryContactFullname",
  "primaryContactEmail",
  "primaryContactPhone",
  "secondaryContactFullname",
  "secondaryContactEmail",
  "secondaryContactPhone",
  "externalLink",
];

const OCDS_TEXT_FIELDS = [
  "externalLink",
  "ocid",
  "noticeType",
  "procurementStage",
  "procedureType",
  "status",
  "cpvCode",
  "cpvDescription",
  "additionalCpvCodes",
  "buyerName",
  "buyerType",
  "buyerStreetAddress",
  "buyerLocality",
  "buyerPostalCode",
  "buyerRegion",
  "buyerCountry",
  "buyerWebsite",
  "contactName",
  "contactEmail",
  "contactTelephone",
  "deliveryRegion",
  "suitability",
];

const OCDS_DATE_FIELDS = [
  "publishedDate",
  "submissionDeadline",
  "enquiryDeadline",
];

const OCDS_NUMBER_FIELDS = ["estimatedValue", "numberOfLots"];

export const ALL_FIELDS = [
  ...TEXT_FIELDS,
  ...OCDS_TEXT_FIELDS,
  ...OCDS_DATE_FIELDS,
  ...OCDS_NUMBER_FIELDS,
  "frameworkAgreement",
  "pscCode",
  "setAside",
  "type",
  "createdDate",
  "dueDate",
];

export const COMMON_FIELDS = [
  "title",
  "description",
  "dueDate",
  "externalLink",
  "estimatedValue",
];

export const TYPE_SPECIFIC_FIELDS = ALL_FIELDS.filter(
  (field) => !COMMON_FIELDS.includes(field)
);

export const SPECIFIC_FIELDS = [];

const getEstimatedValue = (opp) =>
  opp.estimatedValueConverted?.amount ?? opp.estimatedValue ?? "";

export const oppToFormData = (opp) => {
  if (!opp) {
    return INITIAL_NEW_OPPORTUNITY;
  }

  if (opp.opportunityType === MANUAL_OPP_TYPES.OCDS) {
    const ocdsTextDefaults = OCDS_TEXT_FIELDS.reduce((acc, key) => {
      acc[key] = opp[key] ?? "";
      return acc;
    }, {});

    return {
      id: opp.id,
      title: opp.title ?? "",
      description: opp.description ?? "",
      ...ocdsTextDefaults,
      publishedDate: opp.publishedDate ?? "",
      submissionDeadline: opp.submissionDeadline ?? "",
      enquiryDeadline: opp.enquiryDeadline ?? "",
      estimatedValue: getEstimatedValue(opp),
      numberOfLots: opp.numberOfLots ?? "",
      frameworkAgreement: opp.frameworkAgreement ?? false,
    };
  }

  const defaults = TEXT_FIELDS.reduce((acc, key) => {
    acc[key] = opp[key] ?? "";
    return acc;
  }, {});

  return {
    ...defaults,
    id: opp.id,
    pscCode: opp.classificationCode || "",
    setAside: opp.setAside || "",
    type: opp.type || "",
    createdDate: formatDateArrayToYMD(opp.createdDate) || "",
    externalLink: opp?.externalLink ?? "",
    dueDate: formatDateArrayToYMD(opp.dueDate) || "",
    status: opp.active ? "Active" : "Inactive",
    estimatedValue: getEstimatedValue(opp),
  };
};

const isBlank = (value) => {
  if (!value) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim().length === 0;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === "object") {
    return Object.keys(value).length === 0;
  }
  return false;
};

export const resolveProfilePanelSource = (opportunitySource, opportunityType) => {
  if (opportunitySource !== MANUAL_TYPE) {
    return opportunitySource;
  }
  if (opportunityType === US_GOV_CON_TYPE) {
    return SAM_GOV_TYPE;
  }
  if (opportunityType === OCDS_TYPE) {
    return FIND_A_TENDER_TYPE;
  }
  return null;
};

export const hasAnyTypeSpecificFieldFilled = (formData) => {
  return TYPE_SPECIFIC_FIELDS.some((field) => {
    if (field === "status" && formData[field] === INITIAL_NEW_OPPORTUNITY.status) {
      return false;
    }
    return !isBlank(formData[field]);
  });
};
